import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface OptimizationSettings {
  cacheEnabled: boolean;
  batchProcessing: boolean;
  adaptiveRetry: boolean;
  confidenceThreshold: number;
  maxRetries: number;
  timeoutMs: number;
  prefetchFields: boolean;
  parallelProcessing: boolean;
}

interface PerformanceMetrics {
  extractionCount: number;
  averageTime: number;
  cacheHitRate: number;
  errorRate: number;
  retrySuccessRate: number;
}

export function useExtractionOptimization() {
  const [settings, setSettings] = useState<OptimizationSettings>(() => {
    const saved = localStorage.getItem('extraction-optimization-settings');
    return saved ? JSON.parse(saved) : {
      cacheEnabled: true,
      batchProcessing: true,
      adaptiveRetry: true,
      confidenceThreshold: 0.7,
      maxRetries: 3,
      timeoutMs: 30000,
      prefetchFields: true,
      parallelProcessing: true
    };
  });

  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    extractionCount: 0,
    averageTime: 0,
    cacheHitRate: 0,
    errorRate: 0,
    retrySuccessRate: 0
  });

  const [cache, setCache] = useState<Map<string, any>>(new Map());
  const [isOptimizing, setIsOptimizing] = useState(false);
  const { toast } = useToast();

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('extraction-optimization-settings', JSON.stringify(settings));
  }, [settings]);

  // Cache management
  const getCacheKey = useCallback((text: string, options: any = {}) => {
    const key = `${text.slice(0, 100)}_${JSON.stringify(options)}`;
    return btoa(key).slice(0, 32); // Create a shorter, safe cache key
  }, []);

  const getCachedResult = useCallback((cacheKey: string) => {
    if (!settings.cacheEnabled) return null;
    return cache.get(cacheKey);
  }, [cache, settings.cacheEnabled]);

  const setCachedResult = useCallback((cacheKey: string, result: any) => {
    if (!settings.cacheEnabled) return;
    
    // Implement cache size limit
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(cacheKey, {
        ...result,
        cachedAt: Date.now(),
        ttl: 30 * 60 * 1000 // 30 minutes TTL
      });
      return newCache;
    });
  }, [cache, settings.cacheEnabled]);

  // Adaptive retry logic
  const shouldRetry = useCallback((attempt: number, error: any) => {
    if (!settings.adaptiveRetry) return attempt < settings.maxRetries;
    
    // More intelligent retry logic based on error type
    if (error.type === 'network' && attempt < settings.maxRetries) return true;
    if (error.type === 'timeout' && attempt < Math.ceil(settings.maxRetries / 2)) return true;
    if (error.type === 'ai_provider' && attempt < 2) return true;
    
    return false;
  }, [settings.adaptiveRetry, settings.maxRetries]);

  // Optimized extraction function
  const optimizedExtraction = useCallback(async (
    extractFn: (text: string, options: any) => Promise<any>,
    text: string,
    options: any = {}
  ) => {
    const startTime = Date.now();
    const cacheKey = getCacheKey(text, options);
    
    // Check cache first
    const cachedResult = getCachedResult(cacheKey);
    if (cachedResult && (Date.now() - cachedResult.cachedAt) < cachedResult.ttl) {
      updateMetrics({ cacheHit: true, time: Date.now() - startTime });
      return cachedResult;
    }

    let attempt = 0;
    let lastError: any = null;

    while (attempt < settings.maxRetries) {
      try {
        // Apply timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Extraction timeout')), settings.timeoutMs);
        });

        const extractionPromise = extractFn(text, {
          ...options,
          confidenceThreshold: settings.confidenceThreshold,
          parallelProcessing: settings.parallelProcessing
        });

        const result = await Promise.race([extractionPromise, timeoutPromise]);
        
        // Cache successful result
        setCachedResult(cacheKey, result);
        
        updateMetrics({ 
          success: true, 
          time: Date.now() - startTime, 
          attempt,
          cacheHit: false 
        });
        
        return result;
      } catch (error) {
        lastError = error;
        attempt++;
        
        if (shouldRetry(attempt, error)) {
          // Exponential backoff for retries
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          toast({
            title: `Retry Attempt ${attempt}`,
            description: `Retrying extraction after ${delay}ms delay`,
          });
        } else {
          break;
        }
      }
    }

    updateMetrics({ 
      success: false, 
      time: Date.now() - startTime, 
      attempt,
      error: lastError,
      cacheHit: false 
    });
    
    throw lastError;
  }, [
    getCacheKey, 
    getCachedResult, 
    setCachedResult, 
    shouldRetry, 
    settings,
    toast
  ]);

  // Update performance metrics
  const updateMetrics = useCallback((update: {
    success?: boolean;
    time: number;
    attempt?: number;
    error?: any;
    cacheHit: boolean;
  }) => {
    setMetrics(prev => {
      const newCount = prev.extractionCount + 1;
      const newAverageTime = (prev.averageTime * prev.extractionCount + update.time) / newCount;
      
      const cacheHits = update.cacheHit ? 1 : 0;
      const newCacheHitRate = prev.extractionCount > 0 
        ? (prev.cacheHitRate * prev.extractionCount + cacheHits) / newCount
        : cacheHits;

      const errors = update.success === false ? 1 : 0;
      const newErrorRate = prev.extractionCount > 0
        ? (prev.errorRate * prev.extractionCount + errors) / newCount
        : errors;

      const retrySuccesses = update.success && (update.attempt || 0) > 0 ? 1 : 0;
      const newRetrySuccessRate = prev.extractionCount > 0
        ? (prev.retrySuccessRate * prev.extractionCount + retrySuccesses) / newCount
        : retrySuccesses;

      return {
        extractionCount: newCount,
        averageTime: newAverageTime,
        cacheHitRate: newCacheHitRate,
        errorRate: newErrorRate,
        retrySuccessRate: newRetrySuccessRate
      };
    });
  }, []);

  // Optimize settings based on performance
  const optimizeSettings = useCallback(async () => {
    setIsOptimizing(true);
    
    try {
      // Analyze current performance and adjust settings
      const newSettings = { ...settings };
      
      // If error rate is high, increase retries and timeout
      if (metrics.errorRate > 0.2) {
        newSettings.maxRetries = Math.min(settings.maxRetries + 1, 5);
        newSettings.timeoutMs = Math.min(settings.timeoutMs + 5000, 60000);
      }
      
      // If cache hit rate is low, enable more aggressive caching
      if (metrics.cacheHitRate < 0.3) {
        newSettings.cacheEnabled = true;
        newSettings.prefetchFields = true;
      }
      
      // If average time is high, enable parallel processing
      if (metrics.averageTime > 10000) {
        newSettings.parallelProcessing = true;
        newSettings.batchProcessing = true;
      }
      
      setSettings(newSettings);
      
      toast({
        title: "Settings Optimized",
        description: "Extraction settings have been automatically optimized based on performance data",
      });
    } catch (error) {
      toast({
        title: "Optimization Failed",
        description: "Unable to optimize settings automatically",
        variant: "destructive",
      });
    } finally {
      setIsOptimizing(false);
    }
  }, [settings, metrics, toast]);

  // Clear cache
  const clearCache = useCallback(() => {
    setCache(new Map());
    toast({
      title: "Cache Cleared",
      description: "Extraction cache has been cleared",
    });
  }, [toast]);

  // Update individual setting
  const updateSetting = useCallback(<K extends keyof OptimizationSettings>(
    key: K,
    value: OptimizationSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  return {
    // Settings
    settings,
    updateSetting,
    optimizeSettings,
    isOptimizing,
    
    // Performance
    metrics,
    
    // Cache
    clearCache,
    cacheSize: cache.size,
    
    // Optimized extraction
    optimizedExtraction,
  };
}